import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
  Tooltip,
  InputAdornment,
  Popper,
  Paper,
  Snackbar,
  Alert,
  List,
  ListItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SearchIcon from "@mui/icons-material/Search";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { styled } from "@mui/material/styles";
import { ProgramOption } from "../api/types";
import { createFaq, fetchPrograms } from "../api/faqApi";

interface CreateFaqDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: {
    faqs: { question: string; answer: string }[];
    globalVisibility: boolean;
    selectedPrograms: ProgramOption[];
  }) => void;
}

const icon = <CheckBoxOutlineBlankIcon sx={{ fontSize: 20 }} />;
const checkedIcon = <CheckBoxIcon sx={{ fontSize: 20 }} />;

const CustomPopper = (props: any) => (
  <Popper
    {...props}
    style={{
      ...props.style,
      maxHeight: 230,
      overflowY: "auto",
      boxShadow: "0px 4px 16px rgba(0,0,0,0.12)",
      borderRadius: 8,
      background: "#fff",
    }}
    placement="bottom-start"
  />
);

const ActionBar = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-end",
  gap: theme.spacing(1.5),
  padding: theme.spacing(2, 3, 2, 3),
  borderTop: "1px solid #eee",
  background: "#fff",
}));

const CreateFaqDialog: React.FC<CreateFaqDialogProps> = ({
  open,
  onClose,
  onSave,
}) => {
  const [faqItems, setFaqItems] = useState<
    { question: string; answer: string }[]
  >([{ question: "", answer: "" }]);
  const [globalVisibility, setGlobalVisibility] = useState(false);
  const [selectedPrograms, setSelectedPrograms] = useState<ProgramOption[]>([]);
  const [tempSelectedPrograms, setTempSelectedPrograms] = useState<
    ProgramOption[]
  >([]);
  const [inputValue, setInputValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [programs, setPrograms] = useState<ProgramOption[]>([]);
  const [programFetchError, setProgramFetchError] = useState<string | null>(
    null
  );
  const [errors, setErrors] = useState({
    faqs: false,
    programs: false,
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error" | "info" | "warning",
  });
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const loadAllPrograms = async () => {
    setLoading(true);
    setProgramFetchError(null);
    try {
      const allPrograms = await fetchPrograms("");
      setPrograms(allPrograms);
    } catch (error) {
      console.error("Error loading programs:", error);
      setProgramFetchError("Failed to load programs. Please try again.");
      setPrograms([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && !globalVisibility) {
      loadAllPrograms();
    }
  }, [open, globalVisibility]);

  const formatInputValue = (programs: ProgramOption[]) => {
    if (programs.length === 0) {
      return "";
    } else if (programs.length === 1) {
      return `${programs.length} selected`;
    } else {
      return `${programs.length} selected`;
    }
  };

  useEffect(() => {
    // Remove this effect, inputValue should not be automatically set based on selectedPrograms
    // setTempSelectedPrograms(selectedPrograms);
    // setInputValue(formatInputValue(selectedPrograms));
  }, [selectedPrograms]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setInputValue(value);
    setProgramFetchError(null);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const fetchedPrograms = await fetchPrograms(value.trim());
        setPrograms(fetchedPrograms);
      } catch (error) {
        console.error("Error searching programs:", error);
        setProgramFetchError("Failed to search programs. Please try again.");
        setPrograms([]);
      } finally {
        setLoading(false);
      }
    }, 300);
  };

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleProgramSelect = (program: ProgramOption) => {
    const isSelected = tempSelectedPrograms.some(
      (p) => p.value === program.value
    );
    const newSelection = isSelected
      ? tempSelectedPrograms.filter((p) => p.value !== program.value)
      : [...tempSelectedPrograms, program];
    setTempSelectedPrograms(newSelection);
  };

  const handleSelectAll = (e: React.MouseEvent | null) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setTempSelectedPrograms(programs);
    setInputValue(formatInputValue(programs));
  };

  const handleClearAll = (e: React.MouseEvent | null) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setTempSelectedPrograms([]);
    setInputValue("");
  };

  const handleDone = (e: React.MouseEvent | null) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setSelectedPrograms(tempSelectedPrograms);
    setInputValue(formatInputValue(tempSelectedPrograms));
    setIsOpen(false);
  };

  const handleFaqItemChange = (
    index: number,
    field: "question" | "answer",
    value: string
  ) => {
    const newFaqItems = [...faqItems];
    newFaqItems[index] = { ...newFaqItems[index], [field]: value };
    setFaqItems(newFaqItems);
  };

  const handleSave = async () => {
    setErrors({
      faqs: false,
      programs: false,
    });

    const hasEmptyFields = faqItems.some(
      (item) => !item.question.trim() || !item.answer.trim()
    );
    const newErrors = {
      faqs: hasEmptyFields,
      programs:
        !globalVisibility &&
        (!selectedPrograms || selectedPrograms.length === 0),
    };

    setErrors(newErrors);

    if (Object.values(newErrors).some((error) => error)) {
      return;
    }

    setIsSaving(true);

    try {
      const payload = {
        faqs: faqItems.map((item) => ({
          question: item.question.trim(),
          answer: item.answer.trim(),
        })),
        globalVisibility,
        selectedPrograms,
      };

      const response = await createFaq(payload);
      if (response.output?.status) {
        setSnackbar({
          open: true,
          message: "FAQs created successfully",
          severity: "success",
        });
        onSave(payload);
        setFaqItems([{ question: "", answer: "" }]);
        setGlobalVisibility(false);
        setSelectedPrograms([]);
        setTempSelectedPrograms([]);
        setInputValue("");
        setPrograms([]);
        setErrors({ faqs: false, programs: false });
        onClose();
      } else {
        throw new Error(response.output?.message || "Failed to create FAQs");
      }
    } catch (error) {
      console.error("Error creating FAQs:", error);
      setSnackbar({
        open: true,
        message:
          error instanceof Error
            ? error.message
            : "Failed to create FAQs. Please try again.",
        severity: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCloseSnackbar = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: "80vw",
            height: "85vh",
            maxWidth: "80vw",
            maxHeight: "85vh",
            p: 0,
            overflow: "visible",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: "#f7f7f7",
            fontWeight: 700,
            fontSize: 20,
            pb: 2,
            pt: 3,
            px: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid #eee",
            borderRadius: "6px 6px 0 0",
          }}
        >
          Create FAQs
          <IconButton onClick={onClose} sx={{ ml: 2 }}>
            <CloseIcon sx={{ fontSize: 24 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 3, overflowY: "auto", flexGrow: 1 }}>
          {faqItems.map((item, index) => (
            <Box key={index} sx={{ mb: 3, position: "relative" }}>
              <Typography sx={{ fontWeight: 500, py: 1, fontSize: 14 }}>
                Question <span style={{ color: "red" }}>*</span>
              </Typography>
              <TextField
                fullWidth
                value={item.question}
                onChange={(e) =>
                  handleFaqItemChange(index, "question", e.target.value)
                }
                variant="outlined"
                sx={{ mb: 2, background: "#fff" }}
                InputProps={{
                  sx: { fontSize: 14 },
                }}
                error={errors.faqs && !item.question.trim()}
                helperText={
                  errors.faqs && !item.question.trim()
                    ? "Question is required"
                    : ""
                }
              />
              <Typography sx={{ fontWeight: 500, mb: 1, fontSize: 14 }}>
                Answer <span style={{ color: "red" }}>*</span>
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={1}
                value={item.answer}
                onChange={(e) =>
                  handleFaqItemChange(index, "answer", e.target.value)
                }
                variant="outlined"
                sx={{ mb: 1, background: "#fff" }}
                InputProps={{
                  sx: { fontSize: 14 },
                }}
                error={errors.faqs && !item.answer.trim()}
                helperText={
                  errors.faqs && !item.answer.trim() ? "Answer is required" : ""
                }
              />
            </Box>
          ))}

          <FormControlLabel
            control={
              <Checkbox
                checked={globalVisibility}
                onChange={(e) => setGlobalVisibility(e.target.checked)}
                sx={{ mr: 1 }}
              />
            }
            label={
              <Box display="flex" alignItems="center">
                <Typography fontWeight={500} sx={{ fontSize: 14 }}>
                  Global Visibility
                </Typography>
                <Tooltip
                  title="Selecting global visibility will display these FAQs across all programs. Once set to global, program associations cannot be edited or removed."
                  placement="right"
                >
                  <InfoOutlinedIcon
                    sx={{ fontSize: 16, ml: 0.5, color: "#888" }}
                  />
                </Tooltip>
              </Box>
            }
            sx={{ mb: 3, ml: 0 }}
          />
          <Typography sx={{ fontWeight: 500, mb: 1, fontSize: 14 }}>
            List of Programs to which these FAQs are linked{" "}
            {!globalVisibility && <span style={{ color: "red" }}>*</span>}
          </Typography>
          <Box
            sx={{
              border: "1px solid #d3e1e6",
              borderRadius: "10px",
              p: 2,
              mt: 1,
              mb: 3,
              bgcolor: "#fff",
            }}
          >
            <Box sx={{ position: "relative" }}>
              <TextField
                fullWidth
                value={inputValue}
                onChange={handleInputChange}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsOpen(true);
                  if (programs.length === 0 && !loading && !programFetchError) {
                    loadAllPrograms();
                  }
                }}
                onFocus={(e) => {
                  e.stopPropagation();
                  setIsOpen(true);
                  if (programs.length === 0 && !loading && !programFetchError) {
                    loadAllPrograms();
                  }
                }}
                variant="outlined"
                placeholder="Search Programs to Add/Remove"
                disabled={globalVisibility}
                InputProps={{
                  sx: {
                    border: errors.programs
                      ? "1px solid #d32f2f"
                      : "1px solid #d3e1e6",
                    fontSize: 16,
                    bgcolor: "#f9fbfc",
                    borderRadius: "8px",

                    boxSizing: "border-box",
                  },
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "#bdbdbd", fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  background: "#f9fbfc",
                  borderRadius: "8px",
                  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                  border: "1px solid #d3e1e6",
                }}
              />
              {isOpen && !globalVisibility && (
                <Paper
                  sx={{
                    position: "absolute",
                    width: "100%",
                    mt: 1,
                    overflowY: "hidden",
                    zIndex: 1000,
                    boxShadow: "0px 4px 16px rgba(0,0,0,0.12)",
                    borderRadius: "6px",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {loading ? (
                    <Box sx={{ p: 2, textAlign: "center" }}>
                      <Typography>Loading...</Typography>
                    </Box>
                  ) : programFetchError ? (
                    <Box
                      sx={{ p: 2, textAlign: "center", color: "error.main" }}
                    >
                      <Typography>{programFetchError}</Typography>
                    </Box>
                  ) : (
                    <>
                      <Box
                        sx={{
                          maxHeight: 180,
                          overflowY: "auto",
                          flexGrow: 1,
                        }}
                      >
                        {programs.length === 0 && !loading && (
                          <Box
                            sx={{ p: 2, textAlign: "center", color: "#888" }}
                          >
                            <Typography>No programs found.</Typography>
                          </Box>
                        )}
                        <List disablePadding>
                          {programs.map((program) => (
                            <ListItem
                              key={program.value}
                              sx={{
                                py: 0.5,
                                px: 2,
                                fontSize: 14,
                                fontWeight: 400,
                                display: "flex",
                                alignItems: "center",
                                minHeight: 36,
                                cursor: "pointer",
                                "&:hover": {
                                  backgroundColor: "#f5f5f5",
                                },
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleProgramSelect(program);
                              }}
                            >
                              <Checkbox
                                icon={icon}
                                checkedIcon={checkedIcon}
                                checked={tempSelectedPrograms.some(
                                  (p) => p.value === program.value
                                )}
                                sx={{ mr: 2, p: 0 }}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  handleProgramSelect(program);
                                }}
                              />
                              {program.label}
                              <span
                                style={{
                                  fontSize: "14px",
                                  color: "#777",
                                  paddingLeft: 1,
                                }}
                              >
                                ({program.code})
                              </span>
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                      <ActionBar
                        sx={{
                          zIndex: 1,
                          background: "#fff",
                          borderTop: "1px solid #eee",
                          flexShrink: 0,
                        }}
                      >
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectAll(e);
                          }}
                          sx={{ fontWeight: 600, color: "#000" }}
                        >
                          Select All
                        </Button>
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleClearAll(e);
                          }}
                          sx={{
                            fontWeight: 600,
                            color: "#000",
                            border: "1px solid #000",
                            borderRadius: "4px",
                          }}
                        >
                          Clear All
                        </Button>
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDone(e);
                          }}
                          variant="contained"
                          sx={{
                            fontWeight: 600,
                            bgcolor: "#000",
                            color: "#fff",
                            boxShadow: "none",
                            ":hover": { bgcolor: "#222" },
                          }}
                        >
                          Done
                        </Button>
                      </ActionBar>
                    </>
                  )}
                </Paper>
              )}
            </Box>
            {errors.programs && (
              <Typography color="error" sx={{ mt: 1, fontSize: 12 }}>
                Please select at least one program or enable global visibility
              </Typography>
            )}
            {!globalVisibility && selectedPrograms.length === 0 && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: 120,
                  color: "#bdbdbd",
                  mt: 2,
                  textAlign: "center",
                }}
              >
                <img
                  src="/svgs/faqData.svg"
                  alt="No programs selected"
                  style={{ width: 18, height: 18, marginBottom: "8px" }}
                />
                <Typography fontSize={14} color="#929292">
                  No programs selected. Search and select programs or enable
                  Global Visibility.
                </Typography>
              </Box>
            )}
            {!globalVisibility && selectedPrograms.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption">Selected:</Typography>
                <List dense sx={{ maxHeight: 80, overflowY: "auto" }}>
                  {selectedPrograms.map((p) => (
                    <ListItem key={p.value} dense sx={{ py: 0 }}>
                      <Typography variant="body2">{p.label}</Typography>
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </Box>
        </DialogContent>
        <Box sx={{ borderTop: "1px solid #eee", flexShrink: 0 }} />
        <DialogActions sx={{ justifyContent: "flex-end", px: 3, py: 2 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            disabled={isSaving}
            sx={{
              borderRadius: "6px",
              minWidth: 100,
              fontSize: 14,
              fontWeight: 600,
              mr: 2,
              bgcolor: "#fff",
              color: "#000",
              borderColor: "#000",
              ":hover": { bgcolor: "#f7f7f7", borderColor: "#000" },
              padding: "16px 30px",
              height: "42px",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={isSaving}
            sx={{
              borderRadius: 2,
              minWidth: 100,
              fontSize: 14,
              fontWeight: 600,
              bgcolor: "#000",
              color: "#fff",
              boxShadow: "none",
              ":hover": { bgcolor: "#222" },
              padding: "16px 30px",
              height: "42px",
            }}
          >
            {isSaving ? "Creating..." : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity as "success" | "error"}
          icon={
            snackbar.severity === "success" ? (
              <CheckCircleIcon
                sx={{ width: 20, height: 20, color: "#4BAE4F" }}
              />
            ) : undefined
          }
          sx={{
            width: "100%",
            backgroundColor:
              snackbar.severity === "success" ? "#000" : undefined,
            borderRadius: "6px",
            fontSize: 16,
            color: snackbar.severity === "success" ? "#fff" : undefined,
            "& .MuiAlert-icon": {
              color: snackbar.severity === "success" ? "#fff" : undefined,
            },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CreateFaqDialog;
